import React from 'react';
import { useDropzone } from 'react-dropzone';
import { CloudArrowUp } from '@phosphor-icons/react';

import type { FileUploadProps } from './types';
import { formatFileSize } from './utils';

import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export function FileUpload({
  onDropAccepted,
  onDropRejected,
  accept = {
    'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.mp4'],
    'application/pdf': ['.pdf'],
    'text/*': ['.txt', '.csv'],
  },
  maxSize = 5 * 1024 * 1024, // 5MB
  maxFiles = 5,
  multiple = true,
  className,
  disabled = false,
  title = 'Drag & drop files here, or click to select',
  description = '',
  supportsMessage = '',
  children,
}: FileUploadProps) {
  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDropAccepted,
    onDropRejected,
    accept,
    maxSize,
    multiple,
    disabled,
    maxFiles,
  });

  return (
    <div data-testid="file-upload" className="w-full space-y-4">
      <div className={cn('group', className, 'focus-visible:outline-none')} {...getRootProps()}>
        <Card
          className={cn(
            'custom-dashed-border shadow-none p-8 text-center cursor-pointer',
            'group-focus:custom-dashed-border-hover',
            'group-hover:custom-dashed-border-hover',
            isDragActive && !isDragReject && 'border-primary bg-primary/5',
            isDragReject && 'border-destructive bg-destructive/5',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center gap-4">
            <CloudArrowUp
              className={cn(
                'size-12',
                isDragActive && !isDragReject && 'text-primary',
                isDragReject && 'text-destructive',
              )}
            />
            <div className="space-y-2">
              <p className="text-lg font-medium">
                {isDragActive ? (isDragReject ? 'Some files will be rejected' : 'Drop files here') : title}
              </p>
              {description && <p className="text-sm">{description}</p>}
              {supportsMessage ? (
                <p className="text-sm">{supportsMessage}</p>
              ) : (
                <p className="text-sm">
                  {`Supports: ${Object.values(accept).flat().join(', ')} • Max size: ${formatFileSize(maxSize)}${
                    maxFiles > 1 ? ` • Max files: ${maxFiles}` : ''
                  }`}
                </p>
              )}
            </div>
          </div>
        </Card>
      </div>
      {children}
    </div>
  );
}
